import { auth } from '$lib/firebase';
import {
	signInWithEmailAndPassword,
	createUserWithEmailAndPassword,
	sendPasswordResetEmail,
	signOut,
	onAuthStateChanged,
	signInWithPopup,
	GoogleAuthProvider,
	getAdditionalUserInfo,
	type User,
	signInWithCustomToken
} from 'firebase/auth';
import { browser } from '$app/environment';
import { writable, get } from 'svelte/store';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// État réactif de l'utilisateur avec Svelte stores (module-level state requires stores, not runes)
const userStore = writable<User | null>(null);
const loadingStore = writable(browser);
const initializedStore = writable(!browser); // If not in browser, mark as initialized to avoid waiting
const subscriptionDataStore = writable<any>(null);

// Consent options for registration
interface ConsentOptions {
	marketing_consent?: boolean;
	product_updates_consent?: boolean;
}

// Store pending consent to be sent on next sync
let pendingConsent: ConsentOptions | null = null;

// ─── Attribution first-touch ──────────────────────────────────────────────
// Capturée une seule fois par session, au tout premier chargement de l'app.
// Indispensable car au moment du signup, document.referrer pointe sur la page
// interne précédente (/, /pricing…) et non sur la vraie source externe
// (Google, Gemini…). On fige donc la source d'entrée ici et on la relit au
// signup. Stockée en sessionStorage (portée = la visite courante).
const ATTRIBUTION_KEY = 'fs.attribution';
if (browser) {
	try {
		if (!sessionStorage.getItem(ATTRIBUTION_KEY)) {
			const params = new URLSearchParams(window.location.search);
			sessionStorage.setItem(
				ATTRIBUTION_KEY,
				JSON.stringify({
					referrer: document.referrer || '',
					landing_path: window.location.pathname,
					utm_source: params.get('utm_source') || '',
					utm_medium: params.get('utm_medium') || '',
					utm_campaign: params.get('utm_campaign') || ''
				})
			);
		}
	} catch {
		// sessionStorage indisponible (mode privé strict) : on ignore.
	}
}

// Quel CTA a mené à l'inscription. Le landing_path dit d'où vient le visiteur,
// pas ce qui l'a décidé : sans ça, impossible de savoir si une page convertit mal
// parce qu'elle attire le mauvais public ou parce que sa promesse tombe à côté.
// Dernier clic gagnant, portée = la visite courante.
const CTA_KEY = 'fs.cta';

export function noteCtaClick(source: string | undefined) {
	if (!browser || !source) return;
	try {
		sessionStorage.setItem(CTA_KEY, source);
	} catch {
		// sessionStorage indisponible (mode privé strict) : on ignore.
	}
	// Clic anonyme (cta_clicked), le dénominateur de cta_source. text/plain pour
	// rester une requête simple, et sendBeacon pour survivre à la navigation.
	try {
		const body = JSON.stringify({ source, path: location.pathname, connecte: !!get(userStore) });
		navigator.sendBeacon?.(`${API_URL}/api/events/cta`, new Blob([body], { type: 'text/plain' }));
	} catch {
		// Mesure seulement : un échec ne bloque jamais le clic.
	}
}

function getSignupAttribution(): Record<string, string> {
	if (!browser) return {};
	let cta = '';
	try {
		cta = sessionStorage.getItem(CTA_KEY) || '';
	} catch {
		cta = '';
	}
	try {
		const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
		if (raw) return { ...JSON.parse(raw), cta_source: cta };
		return {
			referrer: document.referrer || '',
			landing_path: window.location.pathname,
			cta_source: cta
		};
	} catch {
		return {};
	}
}

// Exposé pour le routing post-signup (signup/+page.svelte) : relit le landing_path
// first-touch figé en sessionStorage.
export function getLandingPath(): string {
	return getSignupAttribution().landing_path || '';
}

// Synchroniser l'utilisateur avec le backend
async function syncUserWithBackend(firebaseUser: User) {
	try {
		// L'identité est portée par le token (header Authorization), pas par le body.
		const body: Record<string, any> = {};

		// Include pending consent if any
		if (pendingConsent) {
			if (pendingConsent.marketing_consent !== undefined) {
				body.marketing_consent = pendingConsent.marketing_consent;
			}
			if (pendingConsent.product_updates_consent !== undefined) {
				body.product_updates_consent = pendingConsent.product_updates_consent;
			}
			pendingConsent = null; // Clear after use
		}

		const token = await firebaseUser.getIdToken();
		const response = await fetch(`${API_URL}/api/auth/ensure-user`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`
			},
			body: JSON.stringify(body)
		});

		if (response.ok) {
			const data = await response.json();
			// Expose primary_intent directly on the subscription object for layout checks
			subscriptionDataStore.set(data.user);
		}
	} catch (error) {
		console.error('Erreur lors de la synchronisation avec le backend:', error);
	}
}

// Tracker dédié au moment de l'auth — appelé depuis register/login/loginWithGoogle
// uniquement, pas depuis onAuthStateChanged (qui re-fire à chaque token refresh).
// Fire-and-forget : le backend tolère un user PG manquant via get_or_create_user.
// `surface` = page où le compte a été créé : /login crée aussi des comptes
// (Google, compte inconnu) sans jamais proposer la case de consentement.
async function trackAuthEvent(
	firebaseUser: User,
	eventName: 'user_signed_up' | 'user_logged_in',
	provider: 'password' | 'google'
) {
	try {
		const token = await firebaseUser.getIdToken();
		const properties =
			eventName === 'user_signed_up'
				? { ...getSignupAttribution(), surface: window.location.pathname, provider }
				: {};
		fetch(`${API_URL}/api/onboarding/track-event`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
			body: JSON.stringify({ event_name: eventName, properties })
		}).catch(() => {});
	} catch {}
}

// Écoute des changements d'état d'authentification
if (browser && auth) {
	onAuthStateChanged(auth, async (firebaseUser) => {
		userStore.set(firebaseUser);

		if (firebaseUser) {
			// Synchroniser avec le backend
			await syncUserWithBackend(firebaseUser);
		} else {
			subscriptionDataStore.set(null);
		}

		loadingStore.set(false);
		initializedStore.set(true);
	});
}

// Fonctions d'authentification
export async function login(email: string, password: string) {
	if (!auth) {
		return { success: false, error: 'Firebase non initialisé' };
	}
	try {
		const userCredential = await signInWithEmailAndPassword(auth, email, password);
		// La synchronisation se fera automatiquement via onAuthStateChanged
		trackAuthEvent(userCredential.user, 'user_logged_in', 'password');
		return { success: true, user: userCredential.user };
	} catch (error: any) {
		return { success: false, error: getErrorMessage(error.code) };
	}
}

// Envoi de l'email de réinitialisation du mot de passe.
// Anti-énumération : `auth/user-not-found` est traité comme un succès, afin que
// l'UI affiche le même message neutre qu'un envoi réussi et ne révèle jamais si
// un compte existe pour une adresse donnée. (Les projets Firebase avec la
// protection d'énumération activée ne lèvent d'ailleurs plus cette erreur.)
// Seules les erreurs techniques (email mal formé, réseau, rate-limit) remontent.
export async function resetPassword(email: string) {
	if (!auth) {
		return { success: false, error: 'Firebase non initialisé' };
	}
	try {
		await sendPasswordResetEmail(auth, email);
		return { success: true };
	} catch (error: any) {
		if (error.code === 'auth/user-not-found') {
			return { success: true };
		}
		return { success: false, error: getErrorMessage(error.code) };
	}
}

export async function register(email: string, password: string, consent?: ConsentOptions) {
	if (!auth) {
		return { success: false, error: 'Firebase non initialisé' };
	}
	try {
		// Store consent to be sent when syncing with backend
		if (consent) {
			pendingConsent = consent;
		}
		const userCredential = await createUserWithEmailAndPassword(auth, email, password);
		// La synchronisation se fera automatiquement via onAuthStateChanged
		trackAuthEvent(userCredential.user, 'user_signed_up', 'password');
		return { success: true, user: userCredential.user };
	} catch (error: any) {
		pendingConsent = null; // Clear on error
		return { success: false, error: getErrorMessage(error.code) };
	}
}

export async function loginWithGoogle(consent?: ConsentOptions) {
	if (!auth) {
		return { success: false, error: 'Firebase non initialisé' };
	}
	try {
		// Store consent to be sent when syncing with backend
		if (consent) {
			pendingConsent = consent;
		}
		const provider = new GoogleAuthProvider();
		const result = await signInWithPopup(auth, provider);
		// La synchronisation se fera automatiquement via onAuthStateChanged
		const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
		trackAuthEvent(result.user, isNewUser ? 'user_signed_up' : 'user_logged_in', 'google');
		return { success: true, user: result.user };
	} catch (error: any) {
		pendingConsent = null; // Clear on error
		return { success: false, error: getErrorMessage(error.code) };
	}
}

export async function logout() {
	if (!auth) {
		return { success: false, error: 'Firebase non initialisé' };
	}
	try {
		await signOut(auth);
		subscriptionDataStore.set(null);
		return { success: true };
	} catch (error: any) {
		return { success: false, error: getErrorMessage(error.code) };
	}
}

// Messages d'erreur en français
function getErrorMessage(errorCode: string): string {
	const errorMessages: Record<string, string> = {
		'auth/email-already-in-use': 'Cet email est déjà utilisé.',
		'auth/invalid-email': 'Adresse email invalide.',
		'auth/operation-not-allowed': 'Opération non autorisée.',
		'auth/weak-password': 'Le mot de passe doit contenir au moins 6 caractères.',
		'auth/user-disabled': 'Ce compte a été désactivé.',
		'auth/user-not-found': 'Aucun compte associé à cet email.',
		'auth/wrong-password': 'Email ou mot de passe incorrect.',
		'auth/too-many-requests': 'Trop de tentatives. Veuillez réessayer plus tard.',
		'auth/network-request-failed': 'Erreur réseau. Vérifiez votre connexion.',
		'auth/invalid-credential': 'Identifiants invalides.'
	};

	return errorMessages[errorCode] || 'Une erreur est survenue. Veuillez réessayer.';
}

// Récupérer le token ID pour les appels API
export async function getIdToken(): Promise<string | null> {
	// Fallback auth.currentUser : juste après register/login, onAuthStateChanged
	// (qui peuple userStore) peut ne pas avoir encore tourné, alors que Firebase a
	// déjà posé auth.currentUser de façon synchrone.
	const currentUser = get(userStore) ?? auth?.currentUser ?? null;
	if (currentUser) {
		try {
			return await currentUser.getIdToken();
		} catch (error) {
			console.error('Erreur lors de la récupération du token:', error);
			return null;
		}
	}
	return null;
}

// Forcer le rafraîchissement des données d'abonnement
export async function refreshSubscriptionData(): Promise<void> {
	const currentUser = get(userStore);
	if (currentUser) {
		try {
			// Force token refresh to get latest custom claims
			await currentUser.getIdToken(true);
			// Sync with backend to get latest subscription data
			await syncUserWithBackend(currentUser);
		} catch (error) {
			console.error('Erreur lors du rafraîchissement des données:', error);
		}
	}
}

// Export des stores pour utilisation dans les composants Svelte
export const authState = {
	user: userStore,
	loading: loadingStore,
	initialized: initializedStore,
	subscription: subscriptionDataStore
};

// Legacy function for backward compatibility (returns store values)
export function getAuthState() {
	return {
		get user() {
			return get(userStore);
		},
		get loading() {
			return get(loadingStore);
		},
		get initialized() {
			return get(initializedStore);
		},
		get subscription() {
			return get(subscriptionDataStore);
		}
	};
}

// Connexion programmatique de dev (panneau navigateur des sessions Claude :
// le popup OAuth y est bloqué). Jamais dans le bundle prod.
if (browser && import.meta.env.DEV && auth) {
	const devAuth = auth;
	(window as any).__devSignInWithToken = (token: string) =>
		signInWithCustomToken(devAuth, token);
}
